import { useCallback, useEffect, useState } from 'react';
import {
  ContactRound,
  IdCard,
  Pencil,
  Plus,
  Search,
  UserRoundCheck,
} from 'lucide-react';
import { ROLES } from '../config/accessControl';
import {
  DRIVER_PERSON_TYPE_LABELS,
  DRIVER_PERSON_TYPES,
  DRIVER_STATUS_META,
  DRIVER_STATUSES,
} from '../config/driverTypes';
import { useAuth } from '../context/AuthContext';
import { driverService } from '../services';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Input from '../components/forms/Input';
import ImageUpload from '../components/forms/ImageUpload';
import Select from '../components/forms/Select';
import Table from '../components/tables/Table';

const personTypeOptions = Object.entries(DRIVER_PERSON_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('es-PY', { dateStyle: 'medium' }).format(new Date(`${value}T12:00:00`))
    : '—';

function DriversPage() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [open, setOpen] = useState(false);
  const [personType, setPersonType] = useState(DRIVER_PERSON_TYPES.PUNTERO);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidateId, setCandidateId] = useState('');
  const [searching, setSearching] = useState(false);
  const [external, setExternal] = useState({ cedula: '', nombres: '', apellidos: '', telefono: '' });
  const [affiliationStatus, setAffiliationStatus] = useState('UNKNOWN');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiration, setLicenseExpiration] = useState('');
  const [licenseFrontImage, setLicenseFrontImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const canCreate = user.role === ROLES.COORDINADOR;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await driverService.getForActor(user);
      setDrivers(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setPersonType(DRIVER_PERSON_TYPES.PUNTERO);
    setQuery('');
    setCandidates([]);
    setCandidateId('');
    setExternal({ cedula: '', nombres: '', apellidos: '', telefono: '' });
    setAffiliationStatus('UNKNOWN');
    setLicenseNumber('');
    setLicenseExpiration('');
    setLicenseFrontImage(null);
    setError('');
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const changeType = (value) => {
    setPersonType(value);
    setQuery('');
    setCandidates([]);
    setCandidateId('');
    setError('');
  };

  const searchCandidates = async () => {
    setSearching(true);
    setError('');
    try {
      const response = await driverService.searchCandidates(personType, query, user);
      setCandidates(response.data);
      setCandidateId(response.data.length === 1 ? response.data[0].id : '');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSearching(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await driverService.createForActor({
        person_type: personType,
        candidate_id: personType === DRIVER_PERSON_TYPES.EXTERNO
          ? undefined
          : Number(candidateId),
        document: external.cedula,
        first_names: external.nombres,
        last_names: external.apellidos,
        phone: external.telefono,
        affiliation_status: personType === DRIVER_PERSON_TYPES.EXTERNO
          ? affiliationStatus
          : selectedCandidate?.affiliation_status || 'UNKNOWN',
        license_number: licenseNumber,
        license_expiration: licenseExpiration,
        license_front_image: licenseFrontImage,
      }, user);
      setOpen(false);
      setFeedback('Conductor presentado y pendiente de aprobación administrativa.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (driver) => {
    setError('');
    try {
      const nextStatus = driver.status === DRIVER_STATUSES.INACTIVE
        ? DRIVER_STATUSES.AVAILABLE
        : DRIVER_STATUSES.INACTIVE;
      await driverService.updateForActor(driver.id, { status: nextStatus }, user);
      setFeedback(nextStatus === DRIVER_STATUSES.INACTIVE ? 'Conductor desactivado.' : 'Conductor activado.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const columns = [
    { key: 'cedula', header: 'Cédula', render: (value) => value || 'No registrada' },
    { key: 'nombreCompleto', header: 'Nombre', render: (value) => <span className="font-semibold text-content-primary">{value}</span> },
    { key: 'person_type', header: 'Tipo', render: (value) => <Badge variant="primary">{DRIVER_PERSON_TYPE_LABELS[value]}</Badge> },
    { key: 'affiliation_status', header: 'Vínculo', render: (value) => value === 'AFFILIATED' ? 'Afiliado/a' : value === 'NOT_AFFILIATED' ? 'No afiliado/a' : 'No informado' },
    { key: 'telefono', header: 'Teléfono', render: (value) => value || '—' },
    { key: 'license_number', header: 'Licencia' },
    { key: 'license_expiration', header: 'Vencimiento', render: formatDate },
    {
      key: 'assignedVehicle',
      header: 'Móvil asignado',
      render: (value) => value
        ? <div><p className="font-medium text-content-primary">{value.plate}</p><p className="text-xs text-content-muted">{value.name}</p></div>
        : <span className="text-content-muted">Sin asignar</span>,
    },
    {
      key: 'approval_status',
      header: 'Revisión',
      render: (value) => <Badge variant={value === 'APPROVED' ? 'success' : value === 'REJECTED' ? 'danger' : 'warning'}>{value === 'APPROVED' ? 'Aprobado' : value === 'REJECTED' ? 'Rechazado' : 'Pendiente'}</Badge>,
    },
    {
      key: 'status',
      header: 'Estado',
      render: (value) => {
        const meta = DRIVER_STATUS_META[value] || { label: value, variant: 'neutral' };
        return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (_, driver) => canCreate && (
        <Button variant="ghost" size="sm" onClick={() => toggleStatus(driver)} disabled={driver.status === DRIVER_STATUSES.ASSIGNED}>
          <Pencil className="size-4" />
          {driver.status === DRIVER_STATUSES.INACTIVE ? 'Activar' : 'Desactivar'}
        </Button>
      ),
    },
  ];

  const selectedCandidate = candidates.find((item) => item.id === candidateId);
  const formComplete = licenseNumber.trim() && licenseExpiration && licenseFrontImage && (
    personType === DRIVER_PERSON_TYPES.EXTERNO
      ? external.cedula && external.nombres.trim() && external.apellidos.trim() && affiliationStatus !== 'UNKNOWN'
      : candidateId
  );

  return (
    <div>
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-content-primary">Conductores</h2>
          <p className="mt-1 text-sm text-content-muted">Personas habilitadas para conducir móviles de la campaña.</p>
        </div>
        {canCreate && <Button onClick={openCreate}><Plus className="size-4" /> Crear conductor</Button>}
      </div>

      {feedback && <Alert variant="success" title="Operación completada" className="mb-5">{feedback}</Alert>}
      {error && !open && <Alert variant="danger" title="No se pudo completar" className="mb-5">{error}</Alert>}

      <Card className="mb-5">
        <div className="flex items-start gap-3">
          <span className="grid size-10 place-items-center rounded-control bg-primary-50 text-primary-700"><IdCard className="size-5" /></span>
          <div><p className="font-semibold text-content-primary">{drivers.length} conductores en la campaña</p><p className="mt-1 text-sm text-content-muted">Los usuarios y votantes se referencian sin crear registros personales adicionales.</p></div>
        </div>
      </Card>

      {loading ? (
        <div className="grid min-h-64 place-items-center"><LoadingSpinner label="Cargando conductores" /></div>
      ) : drivers.length ? (
        <Table columns={columns} data={drivers} />
      ) : (
        <EmptyState icon={ContactRound} title="Sin conductores" description="Todavía no hay conductores asociados a esta estructura y campaña." />
      )}

      <Modal
        open={open}
        onClose={() => !saving && setOpen(false)}
        size="lg"
        title="Crear conductor"
        description="Selecciona una persona existente o registra una persona externa."
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!formComplete}><UserRoundCheck className="size-4" /> Guardar conductor</Button></>}
      >
        <div className="space-y-5">
          {error && <Alert variant="danger" title="No se pudo guardar">{error}</Alert>}
          <Select id="driver-person-type" label="Tipo de conductor" value={personType} onChange={(event) => changeType(event.target.value)} options={personTypeOptions} />

          {personType === DRIVER_PERSON_TYPES.EXTERNO ? (
            <section>
              <Alert variant="info" title="Carga manual" className="mb-4">Utiliza esta opción solamente si la persona no existe como coordinador, puntero o votante.</Alert>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input id="external-document" label="Cédula" required value={external.cedula} onChange={(event) => setExternal((current) => ({ ...current, cedula: event.target.value.replace(/\D/g, '') }))} />
                <Input id="external-phone" label="Teléfono" value={external.telefono} onChange={(event) => setExternal((current) => ({ ...current, telefono: event.target.value }))} />
                <Input id="external-first-name" label="Nombres" required value={external.nombres} onChange={(event) => setExternal((current) => ({ ...current, nombres: event.target.value }))} />
                <Input id="external-last-name" label="Apellidos" required value={external.apellidos} onChange={(event) => setExternal((current) => ({ ...current, apellidos: event.target.value }))} />
                <Select id="external-affiliation" label="Afiliación" required value={affiliationStatus} onChange={(event) => setAffiliationStatus(event.target.value)} options={[{ value: 'AFFILIATED', label: 'Afiliado/a' }, { value: 'NOT_AFFILIATED', label: 'No afiliado/a' }]} placeholder="Seleccionar vínculo" />
              </div>
            </section>
          ) : (
            <section>
              <div className="flex items-end gap-3">
                <Input id="driver-candidate-search" label="Buscar por cédula o nombre" className="flex-1" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Escribe un nombre o cédula" />
                <Button variant="outline" onClick={searchCandidates} loading={searching}><Search className="size-4" /> Buscar</Button>
              </div>
              <div className="mt-3 max-h-44 space-y-2 overflow-y-auto">
                {candidates.map((candidate) => (
                  <button type="button" key={candidate.id} onClick={() => setCandidateId(candidate.id)} className={`flex w-full items-center justify-between rounded-control border p-3 text-left transition ${candidateId === candidate.id ? 'border-primary-400 bg-primary-50' : 'hover:bg-slate-50'}`}>
                    <div><p className="font-medium text-content-primary">{candidate.nombres} {candidate.apellidos}</p><p className="text-xs text-content-muted">{candidate.cedula ? `CI ${candidate.cedula}` : 'Sin cédula registrada'} · {candidate.telefono || 'Sin teléfono'}</p></div>
                    {candidateId === candidate.id && <Badge variant="primary">Seleccionado</Badge>}
                  </button>
                ))}
                {!searching && !candidates.length && <p className="rounded-control bg-slate-50 p-4 text-sm text-content-muted">Realiza una búsqueda para seleccionar una persona de tu estructura.</p>}
              </div>
              {selectedCandidate && <Alert variant="success" title="Persona existente seleccionada" className="mt-3">Se guardará la referencia al registro existente; no se creará otra persona.</Alert>}
            </section>
          )}

          <section className="border-t pt-5">
            <h3 className="mb-3 text-sm font-semibold text-content-primary">Datos de licencia</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input id="driver-license-number" label="Número de licencia" required value={licenseNumber} onChange={(event) => setLicenseNumber(event.target.value)} />
              <Input id="driver-license-expiration" type="date" label="Vencimiento" required value={licenseExpiration} onChange={(event) => setLicenseExpiration(event.target.value)} />
            </div>
            <div className="mt-4">
              <ImageUpload id="driver-license-front" label="Frente del registro de conducir" required value={licenseFrontImage} onChange={setLicenseFrontImage} hint="Imagen obligatoria. Máximo 8 MB." />
            </div>
          </section>
        </div>
      </Modal>
    </div>
  );
}

export default DriversPage;
